import React from "react";
import { Button as ButtonV3 } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Switch } from "@/shared/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Calendar } from "@/shared/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/shared/components/ui/toggle-group";
import { Combobox } from "@/shared/components/ui/combobox";
import { CustomMultiSelect, type Option } from "@/shared/components/CustomMultiSelect";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import { BetterAddressPicker } from "@/shared/components/ui/better-address-picker";
import {
  Settings,
  Plus,
  Minus,
  X,
  Search,
  ArrowRight,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { IconButton } from "@/shared/components/ui/icon-button";

const COMBOBOX_OPTIONS = [
  { value: "react", label: "React" },
  { value: "vue", label: "Vue" },
  { value: "angular", label: "Angular" },
  { value: "svelte", label: "Svelte" },
  { value: "solid", label: "Solid" },
];

const FormControlsPage: React.FC = () => {
  const [radioValue, setRadioValue] = React.useState("option1");
  const [selectValue, setSelectValue] = React.useState("");
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [comboboxValue, setComboboxValue] = React.useState("");
  const [multiSelectValue, setMultiSelectValue] = React.useState<Option[]>([]);
  const [datePickerValue, setDatePickerValue] = React.useState<Date | undefined>();
  const [phoneValue, setPhoneValue] = React.useState("");
  const [addressValue, setAddressValue] = React.useState("");

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">
          Form Controls
        </h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Interactive form elements for user input and data collection.
        </p>
      </header>

      <div className="space-y-16">
        {/* Address Picker */}
        <section id="address-picker" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Address Picker</h2>
            <p className="mt-1 text-sm text-stone-500">
              Google Maps-powered address autocomplete input.
            </p>
          </div>
          <div className="grid w-full gap-1.5">
            <Label htmlFor="address-demo">Address</Label>
            <BetterAddressPicker
              value={addressValue}
              onChange={setAddressValue}
              placeholder="Start typing an address..."
            />
          </div>
        </section>

        {/* Button V3 */}
        <section id="button-v3" className="scroll-mt-8 space-y-6">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <div className="flex items-center gap-2">
              <h2 className="font-sans text-lg font-semibold text-stone-900">Button</h2>
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-sm font-medium text-green-800">
                V3
              </span>
            </div>
            <p className="mt-1 text-sm text-stone-500">
              Button component with variant, size, and rounded options. For icon-only utility
              actions, use{" "}
              <a href="#icon-button" className="underline">
                Icon Button
              </a>{" "}
              instead.
            </p>
          </div>

          {/* Variants Grid */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Variants</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Variant
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Default
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Disabled
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Default</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="default">Default</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="default" disabled>
                        Default
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Brand</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand">Brand</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" disabled>
                        Brand
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Outline</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline">Outline</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" disabled>
                        Outline
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Destructive</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="destructive">Destructive</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="destructive" disabled>
                        Destructive
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Subtle</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="subtle">Subtle</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="subtle" disabled>
                        Subtle
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Ghost</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="ghost">Ghost</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="ghost" disabled>
                        Ghost
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-stone-600">Link</td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="link">Link</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="link" disabled>
                        Link
                      </ButtonV3>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Sizes */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Sizes</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">Size</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Default
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Brand
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Outline
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Small</td>
                    <td className="px-4 py-3">
                      <ButtonV3 size="sm">Small</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" size="sm">
                        Small
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" size="sm">
                        Small
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Medium</td>
                    <td className="px-4 py-3">
                      <ButtonV3 size="md">Medium</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" size="md">
                        Medium
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" size="md">
                        Medium
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">Large</td>
                    <td className="px-4 py-3">
                      <ButtonV3 size="lg">Large</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" size="lg">
                        Large
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" size="lg">
                        Large
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-stone-600">Icon</td>
                    <td className="px-4 py-3">
                      <ButtonV3 size="icon">
                        <Plus className="h-4 w-4" />
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" size="icon">
                        <Plus className="h-4 w-4" />
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" size="icon">
                        <Plus className="h-4 w-4" />
                      </ButtonV3>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Rounded */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Rounded</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Rounded
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Default
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Brand
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-stone-500">
                      Outline
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 text-sm text-stone-600">false (default)</td>
                    <td className="px-4 py-3">
                      <ButtonV3 rounded={false}>Button</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" rounded={false}>
                        Button
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" rounded={false}>
                        Button
                      </ButtonV3>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-stone-600">true (pill)</td>
                    <td className="px-4 py-3">
                      <ButtonV3 rounded>Button</ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="brand" rounded>
                        Button
                      </ButtonV3>
                    </td>
                    <td className="px-4 py-3">
                      <ButtonV3 variant="outline" rounded>
                        Button
                      </ButtonV3>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* With Icons */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">With Icons</h3>
            <div className="flex flex-wrap items-center gap-3">
              <ButtonV3>
                <Plus className="h-4 w-4" />
                Add Document
              </ButtonV3>
              <ButtonV3 variant="brand">
                <Plus className="h-4 w-4" />
                Add Document
              </ButtonV3>
              <ButtonV3 variant="outline">
                <Plus className="h-4 w-4" />
                Add Document
              </ButtonV3>
              <ButtonV3 variant="subtle">
                Continue
                <ArrowRight className="h-4 w-4" />
              </ButtonV3>
            </div>
          </div>

          {/* Icon Only */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Icon Only</h3>
            <p className="text-sm text-stone-500">
              Only for icon actions that need a border or a filled emphasis treatment, such as pager
              arrows and steppers. Every other icon-only action uses{" "}
              <a href="#icon-button" className="underline">
                Icon Button
              </a>
              , which handles the accessible name and tooltip for you.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <ButtonV3 variant="outline" size="icon" aria-label="Previous page">
                <ChevronLeft className="h-4 w-4" />
              </ButtonV3>
              <ButtonV3 variant="outline" size="icon" aria-label="Next page">
                <ChevronRight className="h-4 w-4" />
              </ButtonV3>
              <ButtonV3 variant="outline" size="icon" aria-label="Decrease quantity">
                <Minus className="h-4 w-4" />
              </ButtonV3>
              <ButtonV3 variant="outline" size="icon" aria-label="Increase quantity">
                <Plus className="h-4 w-4" />
              </ButtonV3>
            </div>
          </div>
        </section>

        {/* Calendar */}
        <section id="calendar" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Calendar</h2>
            <p className="mt-1 text-sm text-stone-500">
              Date picker component for selecting dates.
            </p>
          </div>
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            className="rounded-md border"
          />
        </section>

        {/* Checkbox */}
        <section id="checkbox" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Checkbox</h2>
            <p className="mt-1 text-sm text-stone-500">Toggle controls for binary selections.</p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox id="terms" />
              <Label htmlFor="terms">Accept terms and conditions</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="newsletter" defaultChecked />
              <Label htmlFor="newsletter">Subscribe to newsletter</Label>
            </div>
          </div>
        </section>

        {/* Combobox */}
        <section id="combobox" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Combobox</h2>
            <p className="mt-1 text-sm text-stone-500">
              Searchable dropdown with autocomplete functionality.
            </p>
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Framework</Label>
            <Combobox
              options={COMBOBOX_OPTIONS}
              value={comboboxValue}
              onValueChange={setComboboxValue}
              placeholder="Select a framework..."
              searchPlaceholder="Search frameworks..."
            />
          </div>
        </section>

        {/* Multi Select */}
        <section id="multi-select" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Multi Select</h2>
            <p className="mt-1 text-sm text-secondary">
              Dropdown for selecting several options at once. Selected items appear as removable
              tags, with the overflow count shown as a matching tag.
            </p>
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Frameworks</Label>
            <CustomMultiSelect
              options={COMBOBOX_OPTIONS}
              selected={multiSelectValue}
              onChange={setMultiSelectValue}
              placeholder="Select frameworks..."
            />
          </div>
        </section>

        {/* Date Picker */}
        <section id="date-picker" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Date Picker</h2>
            <p className="mt-1 text-sm text-stone-500">
              Text input with calendar popover for date selection.
            </p>
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Date</Label>
            <DatePicker value={datePickerValue} onChange={setDatePickerValue} />
          </div>
        </section>

        {/* Icon Button */}
        <section id="icon-button" className="scroll-mt-8 space-y-6">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Icon Button</h2>
            <p className="mt-1 text-sm text-stone-500">
              Utility buttons for icon-only actions. The required label prop sets the accessible
              name and doubles as the tooltip; disable the tooltip for self-evident controls such as
              a dialog close.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium">Sizes</h3>
            <div className="flex flex-wrap items-center gap-3">
              <IconButton size="sm" label="Settings">
                <Settings />
              </IconButton>
              <IconButton size="md" label="Settings">
                <Settings />
              </IconButton>
              <IconButton size="lg" label="Settings">
                <Settings />
              </IconButton>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium">Neutral</h3>
            <div className="flex flex-wrap items-center gap-3">
              <IconButton size="sm" label="Search">
                <Search />
              </IconButton>
              <IconButton size="md" label="Search">
                <Search />
              </IconButton>
              <IconButton size="lg" label="Search">
                <Search />
              </IconButton>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium">Destructive</h3>
            <div className="flex flex-wrap items-center gap-3">
              <IconButton size="sm" variant="destructive" label="Delete">
                <Trash2 />
              </IconButton>
              <IconButton size="md" variant="destructive" label="Delete">
                <Trash2 />
              </IconButton>
              <IconButton size="lg" variant="destructive" label="Delete">
                <Trash2 />
              </IconButton>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium">Inverse</h3>
            <div className="flex flex-wrap items-center gap-3 rounded-md bg-inverse p-3">
              <IconButton size="sm" variant="inverse" label="Add">
                <Plus />
              </IconButton>
              <IconButton size="md" variant="inverse" label="Add">
                <Plus />
              </IconButton>
              <IconButton size="lg" variant="inverse" label="Add">
                <Plus />
              </IconButton>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium">Disabled</h3>
            <div className="flex flex-wrap items-center gap-3">
              <IconButton size="sm" disabled label="Settings">
                <Settings />
              </IconButton>
              <IconButton size="md" disabled label="Settings">
                <Settings />
              </IconButton>
              <IconButton size="lg" disabled label="Settings">
                <Settings />
              </IconButton>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium">Close</h3>
            <div className="flex flex-wrap items-center gap-3">
              <IconButton size="sm" tooltip={false} label="Close">
                <X />
              </IconButton>
              <IconButton size="md" tooltip={false} label="Close">
                <X />
              </IconButton>
              <IconButton size="lg" tooltip={false} label="Close">
                <X />
              </IconButton>
            </div>
          </div>
        </section>

        {/* Input */}
        <section id="input" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Input</h2>
            <p className="mt-1 text-sm text-stone-500">
              Text input fields for collecting user data.
            </p>
          </div>
          <div className="grid w-full gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input type="email" id="email" placeholder="Enter your email" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input type="password" id="password" placeholder="Enter your password" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="disabled">Disabled</Label>
              <Input id="disabled" placeholder="Disabled input" disabled />
            </div>
          </div>
        </section>

        {/* Label */}
        <section id="label" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Label</h2>
            <p className="mt-1 text-sm text-stone-500">Accessible labels for form controls.</p>
          </div>
          <div className="grid w-full gap-1.5">
            <Label htmlFor="label-demo">Label Demo</Label>
            <Input id="label-demo" placeholder="This input has a label" />
          </div>
        </section>

        {/* Phone Number Input */}
        <section id="phone-number-input" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Phone Number Input</h2>
            <p className="mt-1 text-sm text-stone-500">
              Formatted phone number input with automatic formatting.
            </p>
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label htmlFor="phone-demo">Phone Number</Label>
            <PhoneNumberInput value={phoneValue} onChange={setPhoneValue} />
            {phoneValue && <p className="text-sm text-stone-500">Raw value: {phoneValue}</p>}
          </div>
        </section>

        {/* Radio Group */}
        <section id="radio-group" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Radio Group</h2>
            <p className="mt-1 text-sm text-stone-500">
              Single selection controls from a set of options.
            </p>
          </div>
          <RadioGroup value={radioValue} onValueChange={setRadioValue}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="option1" id="option1" />
              <Label htmlFor="option1">Option 1</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="option2" id="option2" />
              <Label htmlFor="option2">Option 2</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="option3" id="option3" />
              <Label htmlFor="option3">Option 3</Label>
            </div>
          </RadioGroup>
        </section>

        {/* Select */}
        <section id="select" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Select</h2>
            <p className="mt-1 text-sm text-stone-500">
              Dropdown selection from predefined options.
            </p>
          </div>
          <Select value={selectValue} onValueChange={setSelectValue}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="option1">Option 1</SelectItem>
              <SelectItem value="option2">Option 2</SelectItem>
              <SelectItem value="option3">Option 3</SelectItem>
            </SelectContent>
          </Select>
        </section>

        {/* Switch */}
        <section id="switch" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Switch</h2>
            <p className="mt-1 text-sm text-stone-500">Toggle control for on/off states.</p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Switch id="airplane-mode" />
              <Label htmlFor="airplane-mode">Airplane Mode</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="wifi" defaultChecked />
              <Label htmlFor="wifi">Wi-Fi</Label>
            </div>
          </div>
        </section>

        {/* Textarea */}
        <section id="textarea" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Textarea</h2>
            <p className="mt-1 text-sm text-stone-500">Multi-line text input for longer content.</p>
          </div>
          <div className="grid w-full gap-1.5">
            <Label htmlFor="message">Message</Label>
            <Textarea placeholder="Type your message here." id="message" />
          </div>
        </section>

        {/* Toggle Group */}
        <section id="toggle-group" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Toggle Group</h2>
            <p className="mt-1 text-sm text-stone-500">
              Group of toggle buttons for single or multi-selection.
            </p>
          </div>
          <ToggleGroup type="single">
            <ToggleGroupItem value="bold">Bold</ToggleGroupItem>
            <ToggleGroupItem value="italic">Italic</ToggleGroupItem>
            <ToggleGroupItem value="underline">Underline</ToggleGroupItem>
          </ToggleGroup>
        </section>
      </div>
    </div>
  );
};

export default FormControlsPage;
